// รูปสินค้า (D-143 ส่วนที่ 2) — แสดง/อัปโหลด/ถ่ายรูป/ตั้งรูปหลัก/ถอด; client/resizeImageToLimit FREEZE ไว้แล้ว (WO-025/026)

import { useEffect, useRef, useState, type ChangeEvent } from 'react';

import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { StatusBadge } from '../StatusBadge';
import {
  fetchProductImageBlob,
  listProductImages,
  removeProductImage,
  setPrimaryProductImage,
  uploadProductImage,
  type ProductFailure,
} from '../../lib/products/client';
import { PRODUCT_MASTER_MODULE, type ProductImage } from '../../lib/products/types';
import { resizeImageToLimit } from '../../lib/images/resizeImage';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';

export interface ProductImagesPanelProps {
  readonly productId: string;
  readonly productStatus: string;
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

/** สถานะเดียวที่ล็อกสินค้าไว้ (contract: `PATCH`/คำสั่งแก้ไขอื่นปิดช่วงรออนุมัติ) */
const LOCKED_STATUS = 'PENDING_APPROVAL';

/** โหลดรูปพร้อมกันได้ไม่เกินนี้ — กันคำขอ `.../content` ยิงรัวทั้งหน้าตอนมีรูปจำนวนมาก */
const MAX_CONCURRENT_BLOB_LOADS = 3;

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly ProductImage[] }
  | { readonly status: 'failed'; readonly failure: ProductFailure };

type BlobEntry =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly url: string }
  | { readonly kind: 'failed' };

type RowAction = 'primary' | 'remove';

interface FileLogEntry {
  readonly filename: string;
  readonly notice: string | null;
  readonly error: string | null;
}

type UploadSession =
  | { readonly status: 'idle' }
  | { readonly status: 'running'; readonly label: string; readonly log: readonly FileLogEntry[] }
  | {
      readonly status: 'done';
      readonly log: readonly FileLogEntry[];
      readonly successCount: number;
      readonly failureCount: number;
    };

/** ข้อความจากความล้มเหลว — แสดงข้อความจริงของเซิร์ฟเวอร์เมื่อมี ไม่เดาแทน */
function productImageFailureMessage(failure: ProductFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'ต้องเข้าสู่ระบบก่อนจึงจะใช้งานรูปสินค้าได้';
    case 'forbidden':
      return failure.error.message ?? 'เซิร์ฟเวอร์ตอบ 403 — ไม่มีสิทธิ์ทำรายการนี้';
    case 'not-found':
      return failure.error.message ?? 'เซิร์ฟเวอร์ตอบ 404 — ไม่พบรายการนี้';
    case 'conflict':
      return failure.error.message ?? 'เซิร์ฟเวอร์ตอบ 409 — มีการเปลี่ยนแปลงไปแล้ว';
    case 'rule-violation':
      return failure.error.message ?? 'เซิร์ฟเวอร์ปฏิเสธคำขอนี้ตามกฎทางธุรกิจ';
    case 'bad-request':
      return failure.error.message ?? 'เซิร์ฟเวอร์ตอบ 400 — คำขอไม่ถูกต้อง';
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`;
    case 'network-error':
      return failure.detail;
    case 'invalid-response':
      return 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ';
    case 'cancelled':
      return 'ยกเลิกคำขอแล้ว';
  }
}

/** ตัดทศนิยมที่ไม่จำเป็นทิ้ง — 8388608 ไบต์ต้องอ่านว่า "8" ไม่ใช่ "8.0" */
function formatMegabytes(bytes: number): string {
  const rounded = Math.round((bytes / (1024 * 1024)) * 10) / 10;
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${formatMegabytes(bytes)} MB`;
}

function resizeFailureMessage(
  filename: string,
  status: 'decode-failed' | 'too-large-after-resize' | 'unsupported-type',
): string {
  switch (status) {
    case 'decode-failed':
      return `อ่านไฟล์ ${filename} เป็นรูปไม่สำเร็จ — ไม่ได้อัปโหลด`;
    case 'too-large-after-resize':
      return `ย่อรูป ${filename} แล้วยังเกิน 5 MB — ไม่ได้อัปโหลด`;
    case 'unsupported-type':
      return `${filename} ไม่ใช่ไฟล์รูปที่รองรับ — ไม่ได้อัปโหลด`;
  }
}

export function ProductImagesPanel({
  productId,
  productStatus,
  permissions,
  apiBaseUrl,
  fetchImpl,
}: ProductImagesPanelProps) {
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [blobEntries, setBlobEntries] = useState<Readonly<Record<string, BlobEntry>>>({});
  const blobEntriesRef = useRef(blobEntries);
  const [viewing, setViewing] = useState<ProductImage | null>(null);
  const [session, setSession] = useState<UploadSession>({ status: 'idle' });
  const [rowBusy, setRowBusy] = useState<{ readonly imageId: string; readonly action: RowAction } | null>(
    null,
  );
  const [rowErrors, setRowErrors] = useState<Readonly<Record<string, string>>>({});
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const canEdit =
    can(permissions, PRODUCT_MASTER_MODULE, 'EDIT') && productStatus !== LOCKED_STATUS;
  // เดสก์ท็อปไม่มีกล้องให้ capture เปิดจริง ปุ่ม "ถ่ายรูป" จึงไม่มีความหมายและไม่แสดง
  const isTouchDevice = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

  useEffect(() => {
    blobEntriesRef.current = blobEntries;
  }, [blobEntries]);

  // reloadToken รวมทาง refresh ทั้งหมด (ลองใหม่/อัปโหลดสำเร็จ/ตั้งรูปหลัก/ถอดสำเร็จ) ไว้จุดเดียว
  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setList({ status: 'loading' });

    void listProductImages(productId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status === 'ready') {
        setList({ status: 'ready', items: result.page.items });
      } else {
        setList({ status: 'failed', failure: result });
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [productId, apiBaseUrl, fetchImpl, reloadToken]);

  // โหลดไบต์รูปทีละไม่เกิน MAX_CONCURRENT_BLOB_LOADS — รูปที่หลุดจากรายการ revoke ทันที ไม่รอ unmount
  useEffect(() => {
    if (list.status !== 'ready') {
      return;
    }
    let active = true;
    const currentIds = new Set(list.items.map((item) => item.image_id));

    setBlobEntries((prev) => {
      const next: Record<string, BlobEntry> = {};
      for (const [id, entry] of Object.entries(prev)) {
        if (currentIds.has(id)) {
          next[id] = entry;
        } else if (entry.kind === 'ready') {
          URL.revokeObjectURL(entry.url);
        }
      }
      return next;
    });

    const pending = list.items.filter(
      (item) => !(item.image_id in blobEntriesRef.current),
    );

    const worker = async () => {
      while (active) {
        const item = pending.shift();
        if (item === undefined) {
          return;
        }
        setBlobEntries((prev) => ({ ...prev, [item.image_id]: { kind: 'loading' } }));
        const result = await fetchProductImageBlob(productId, item.image_id, {
          apiBaseUrl,
          fetchImpl,
        });
        if (!active) {
          return;
        }
        if (result.status === 'ready') {
          const url = URL.createObjectURL(result.blob);
          setBlobEntries((prev) => ({ ...prev, [item.image_id]: { kind: 'ready', url } }));
        } else {
          setBlobEntries((prev) => ({ ...prev, [item.image_id]: { kind: 'failed' } }));
        }
      }
    };

    const workerCount = Math.min(MAX_CONCURRENT_BLOB_LOADS, pending.length);
    for (let i = 0; i < workerCount; i += 1) {
      void worker();
    }

    return () => {
      active = false;
    };
  }, [list, productId, apiBaseUrl, fetchImpl]);

  // unmount — revoke ทุก object URL ที่ยังถืออยู่ ไม่ว่าจะมากี่รูป
  useEffect(() => {
    return () => {
      for (const entry of Object.values(blobEntriesRef.current)) {
        if (entry.kind === 'ready') {
          URL.revokeObjectURL(entry.url);
        }
      }
    };
  }, []);

  async function handleFiles(files: readonly File[]) {
    if (files.length === 0) {
      return;
    }
    const log: FileLogEntry[] = [];
    let successCount = 0;
    let failureCount = 0;
    const total = files.length;

    for (let index = 0; index < total; index += 1) {
      const file = files[index];
      const position = index + 1;

      setSession({
        status: 'running',
        label: `กำลังตรวจสอบขนาด ${file.name} (${position}/${total})…`,
        log: [...log],
      });

      const resized = await resizeImageToLimit(file, file.name);
      if (resized.status !== 'ok') {
        failureCount += 1;
        log.push({
          filename: file.name,
          notice: null,
          error: resizeFailureMessage(file.name, resized.status),
        });
        continue;
      }

      const notice = resized.resized
        ? `ย่อรูป ${file.name} จาก ${formatMegabytes(resized.originalBytes)} MB เป็น ${formatMegabytes(
            resized.finalBytes,
          )} MB แล้ว`
        : null;

      if (notice !== null) {
        setSession({ status: 'running', label: notice, log: [...log] });
      }
      setSession({
        status: 'running',
        label: `กำลังอัปโหลด ${resized.filename} (${position}/${total})…`,
        log: [...log],
      });

      const uploaded = await uploadProductImage(
        productId,
        resized.blob,
        { filename: resized.filename, contentType: resized.contentType },
        { apiBaseUrl, fetchImpl },
      );

      if (uploaded.status === 'ready') {
        successCount += 1;
        log.push({ filename: file.name, notice, error: null });
        setReloadToken((token) => token + 1);
      } else {
        failureCount += 1;
        log.push({ filename: file.name, notice, error: productImageFailureMessage(uploaded) });
      }
    }

    setSession({ status: 'done', log, successCount, failureCount });
  }

  function onFilesSelected(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const files = input.files === null ? [] : Array.from(input.files);
    input.value = '';
    void handleFiles(files);
  }

  async function handleSetPrimary(imageId: string) {
    setRowBusy({ imageId, action: 'primary' });
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[imageId];
      return next;
    });
    const result = await setPrimaryProductImage(productId, imageId, { apiBaseUrl, fetchImpl });
    setRowBusy(null);
    if (result.status === 'ready') {
      setReloadToken((token) => token + 1);
    } else {
      setRowErrors((prev) => ({ ...prev, [imageId]: productImageFailureMessage(result) }));
    }
  }

  async function handleRemove(imageId: string) {
    setConfirmingRemoveId(null);
    setRowBusy({ imageId, action: 'remove' });
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[imageId];
      return next;
    });
    const result = await removeProductImage(productId, imageId, { apiBaseUrl, fetchImpl });
    setRowBusy(null);
    if (result.status === 'ready') {
      setReloadToken((token) => token + 1);
    } else {
      setRowErrors((prev) => ({ ...prev, [imageId]: productImageFailureMessage(result) }));
    }
  }

  const uploadBusy = session.status === 'running';
  const viewingBlob = viewing === null ? undefined : blobEntries[viewing.image_id];

  return (
    <section className="cap-card cap-card--table" aria-label="รูปสินค้า" data-testid="product-images-panel">
      <div className="cap-card__head">
        <h2 className="cap-card__title">
          รูปสินค้า
          <InfoHint text="ระบบย่อรูปให้อัตโนมัติถ้าเกิน 5 MB ก่อนอัปโหลด" />
        </h2>
        {canEdit ? (
          <div className="cap-form__actions">
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="product-images-pick"
              disabled={uploadBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              เลือกรูป
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              data-testid="product-images-file-input"
              onChange={onFilesSelected}
            />
            {isTouchDevice ? (
              <>
                <button
                  type="button"
                  className="cap-button cap-button--ghost"
                  data-testid="product-images-camera"
                  disabled={uploadBusy}
                  onClick={() => cameraInputRef.current?.click()}
                >
                  ถ่ายรูป
                </button>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  data-testid="product-images-camera-input"
                  onChange={onFilesSelected}
                />
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {session.status === 'idle' ? null : (
        <div className="cap-card__foot" role="status" data-testid="product-images-upload-status">
          {session.status === 'running' ? (
            <p>{session.label}</p>
          ) : (
            <p data-testid="product-images-summary">
              อัปโหลดสำเร็จ {session.successCount} รูป, ไม่สำเร็จ {session.failureCount} รูป
            </p>
          )}
          {session.log.map((entry, index) => (
            <p key={`${entry.filename}-${index}`} data-testid={`product-images-log-${index}`}>
              {entry.notice ?? ''}
              {entry.notice !== null && entry.error !== null ? ' — ' : ''}
              {entry.error ?? ''}
            </p>
          ))}
        </div>
      )}

      {list.status === 'loading' ? (
        <p className="cap-card__foot" data-testid="product-images-loading" role="status">
          กำลังโหลดรูปสินค้า…
        </p>
      ) : null}

      {list.status === 'failed' ? (
        <div className="cap-card__foot" data-testid="product-images-failure">
          <p>{productImageFailureMessage(list.failure)}</p>
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="product-images-retry"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            ลองใหม่
          </button>
        </div>
      ) : null}

      {list.status === 'ready' && list.items.length === 0 ? (
        <p className="cap-card__foot" data-testid="product-images-empty">
          ยังไม่มีรูปสินค้า
        </p>
      ) : null}

      {list.status === 'ready' && list.items.length > 0 ? (
        <div className="cap-images-grid" data-testid="product-images-grid">
          {list.items.map((item) => {
            const blob = blobEntries[item.image_id];
            const busy = rowBusy !== null && rowBusy.imageId === item.image_id;
            const confirming = confirmingRemoveId === item.image_id;
            const rowError = rowErrors[item.image_id];

            return (
              <div
                className="cap-image-tile"
                data-testid={`product-image-${item.image_id}`}
                key={item.image_id}
              >
                {blob?.kind === 'ready' ? (
                  <button
                    type="button"
                    className="cap-image-tile__thumb"
                    data-testid={`product-image-open-${item.image_id}`}
                    onClick={() => setViewing(item)}
                  >
                    <img src={blob.url} alt={item.filename} />
                  </button>
                ) : (
                  <div className="cap-image-tile__placeholder">
                    {blob?.kind === 'failed' ? 'โหลดรูปไม่สำเร็จ' : 'กำลังโหลด…'}
                  </div>
                )}

                <div className="cap-image-tile__meta">
                  <span className="cap-image-tile__filename">{item.filename}</span>
                  <span className="cap-image-tile__size">{formatFileSize(item.size_bytes)}</span>
                  {item.is_primary ? <StatusBadge tone="success">รูปหลัก</StatusBadge> : null}
                </div>

                {canEdit ? (
                  <div className="cap-image-tile__actions">
                    {item.is_primary ? null : (
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        data-testid={`product-image-set-primary-${item.image_id}`}
                        disabled={busy}
                        onClick={() => void handleSetPrimary(item.image_id)}
                      >
                        ตั้งเป็นรูปหลัก
                      </button>
                    )}

                    {confirming ? (
                      <>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`product-image-remove-confirm-${item.image_id}`}
                          disabled={busy}
                          onClick={() => void handleRemove(item.image_id)}
                        >
                          ยืนยันถอด
                        </button>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`product-image-remove-cancel-${item.image_id}`}
                          disabled={busy}
                          onClick={() => setConfirmingRemoveId(null)}
                        >
                          ไม่ถอด
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        data-testid={`product-image-remove-${item.image_id}`}
                        disabled={busy}
                        onClick={() => setConfirmingRemoveId(item.image_id)}
                      >
                        ถอดรูป
                      </button>
                    )}
                  </div>
                ) : null}

                {rowError === undefined ? null : (
                  <p
                    className="cap-image-tile__error"
                    data-testid={`product-image-error-${item.image_id}`}
                  >
                    {rowError}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={viewing === null ? 'รูปสินค้า' : viewing.filename}
      >
        {viewing === null ? null : viewingBlob?.kind === 'ready' ? (
          <img className="cap-image-tile__full" src={viewingBlob.url} alt={viewing.filename} />
        ) : (
          <p>โหลดรูปไม่สำเร็จ</p>
        )}
      </Modal>
    </section>
  );
}
